/* eslint-disable function-paren-newline,max-lines-per-function,max-statements */
/* eslint-disable no-use-before-define,@typescript-eslint/no-use-before-define */
/* eslint-disable object-curly-newline,sort-imports-es6-autofix/sort-imports-es6 */

import { Params } from '@angular/router';
import {
  Organization, OrganizationSubType, OrganizationSubTypeForm,
} from '@common/entities';
import { WhereFilter } from '@common/filter.decorator';

import { FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID } from 'src/app/@shared/form/form-field-config';
import { FormKey } from 'src/app/@shared/form/form-config';
import {
  OrganizationFilterForm,
} from 'src/app/@shared/form/form.types';
import { getConfigs } from 'src/app/@shared/form/form-helper';
import {
  getFilterResultFromQueryParamsByConfig,
  normalizeFilterResultByConfig,
} from 'src/app/@shared/filter-form-value.adapter';
import { OrganizationSource } from 'src/app/organization/@shared/organization.model';

export type OrganizationFilterSubset = Pick<OrganizationFilterForm,
'status' | 'addressFederalState' | 'isPublished' | 'hasTransparencyLabel' | 'language' | 'qualifications' | 'media' |
  'searchForms' | 'stateLevelSubdivisions' | 'subType' | 'subTypeForm' | 'targetGroup'>;

export type OrganizationFilterResultForWhere = Partial<OrganizationFilterSubset> & {
  topics?: string[];
};

export function normalizeOrganizationFilterResult(
  rawFilterResult: OrganizationFilterResultForWhere,
  formId: FormKey = 'filterOrganization',
): OrganizationFilterResultForWhere {
  const { formConfig } = getConfigs(formId);
  const filterResult = normalizeFilterResultByConfig(rawFilterResult as Record<string, unknown>, formConfig) as OrganizationFilterResultForWhere;

  return normalizeOrganizationFilterResultForWhere(filterResult);
}

export function getOrganizationFilterResultByQueryParams(
  queryParams: Params,
  filterKeys: string[],
  formId: FormKey = 'filterOrganization',
): OrganizationFilterResultForWhere {
  const { formConfig } = getConfigs(formId);

  return getFilterResultFromQueryParamsByConfig<OrganizationFilterResultForWhere>(
    queryParams,
    formConfig,
    filterKeys,
    [
      'qualifications',
      'media',
      'searchForms',
      'stateLevelSubdivisions',
      'subType',
      'subTypeForm',
      'targetGroup',
      'topics',
    ],
  ) as OrganizationFilterResultForWhere;
}

export function normalizeOrganizationFilterResultForWhere(
  filterResult: OrganizationFilterResultForWhere,
): OrganizationFilterResultForWhere {
  const selectedSubTypes = (filterResult.subType || []) as OrganizationSubType[];
  const selectedSubTypeForms = (filterResult.subTypeForm || []) as OrganizationSubTypeForm[];

  if (!selectedSubTypes.length || !selectedSubTypeForms.length) {
    return filterResult;
  }

  const subTypesCoveredBySelectedForms = new Set(
    Object.entries(FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID)
      .filter(([subType, forms]) => {
        return subType !== 'all'
          && forms.length
          && selectedSubTypeForms.some((selectedSubTypeForm) => {
            return forms.includes(selectedSubTypeForm);
          });
      })
      .map(([subType]) => {
        return subType as OrganizationSubType;
      }),
  );
  const remainingSubTypes = selectedSubTypes.filter((subType) => {
    return !subTypesCoveredBySelectedForms.has(subType);
  });

  if (remainingSubTypes.length) {
    return {
      ...filterResult,
      subType: remainingSubTypes,
    };
  }

  const normalizedFilterResult = {
    ...filterResult,
  };

  delete normalizedFilterResult.subType;

  return normalizedFilterResult;
}

export function getOrganizationWhereFilterByFilterResult(
  rawFilterResult: OrganizationFilterResultForWhere,
  formId: FormKey = 'filterOrganization',
): WhereFilter<OrganizationSource> {
  const filterResult = normalizeOrganizationFilterResult(rawFilterResult, formId);
  const where = getOrganizationArrayWhereFilter(filterResult);
  let normalizedIsPublished: boolean | undefined;
  let normalizedHasTransparencyLabel: boolean | undefined;
  const isPublishedFilterValue = (filterResult as Record<string, unknown>).isPublished;
  const hasTransparencyLabelFilterValue = (filterResult as Record<string, unknown>).hasTransparencyLabel;

  if (isPublishedFilterValue === true || isPublishedFilterValue === 'true') {
    normalizedIsPublished = true;
  } else if (isPublishedFilterValue === false || isPublishedFilterValue === 'false') {
    normalizedIsPublished = false;
  }

  if (hasTransparencyLabelFilterValue === true || hasTransparencyLabelFilterValue === 'true') {
    normalizedHasTransparencyLabel = true;
  } else if (hasTransparencyLabelFilterValue === false || hasTransparencyLabelFilterValue === 'false') {
    normalizedHasTransparencyLabel = false;
  }

  if (filterResult.status) {
    where.status = filterResult.status;
  }

  if (filterResult.addressFederalState) {
    where.addressFederalState = filterResult.addressFederalState;
  }

  if (filterResult.language) {
    where.language = filterResult.language;
  }

  if (typeof normalizedIsPublished === 'boolean') {
    where.isPublished = normalizedIsPublished;
  }

  if (typeof normalizedHasTransparencyLabel === 'boolean') {
    where.hasTransparencyLabel = normalizedHasTransparencyLabel;
  }

  const selectedSubTypes = (filterResult.subType || []) as OrganizationSubType[];
  const selectedSubTypeForms = (filterResult.subTypeForm || []) as OrganizationSubTypeForm[];
  const topicIds = (filterResult.topics || []) as string[];

  const subTypeBranches = getOrganizationSubTypeBranches(selectedSubTypes, selectedSubTypeForms);
  const topicBranches = getOrganizationTopicBranches(topicIds);

  const combinedOrBranches = combineOrBranchGroups([subTypeBranches, topicBranches]);

  if (combinedOrBranches.length) {
    where.or = combinedOrBranches;
  }

  return where;
}

export function getOrganizationFilterResultByWhereFilter(
  whereFilter: WhereFilter<Organization> | undefined,
): OrganizationFilterResultForWhere {
  if (!whereFilter) {
    return {};
  }

  const filterResult: OrganizationFilterResultForWhere = {};

  if (whereFilter.status) {
    filterResult.status = whereFilter.status as any;
  }

  if (whereFilter.addressFederalState) {
    filterResult.addressFederalState = whereFilter.addressFederalState as string;
  }

  if (whereFilter.language) {
    (filterResult as Record<string, unknown>).language = whereFilter.language;
  }

  if (typeof whereFilter.isPublished === 'boolean') {
    filterResult.isPublished = whereFilter.isPublished;
  }

  if (typeof whereFilter.hasTransparencyLabel === 'boolean') {
    filterResult.hasTransparencyLabel = whereFilter.hasTransparencyLabel;
  }

  const qualifications = getStringArrayFromContainsFilter(whereFilter.qualifications);

  if (qualifications.length) {
    filterResult.qualifications = qualifications;
  }

  const media = getStringArrayFromContainsFilter(whereFilter.media);

  if (media.length) {
    filterResult.media = media;
  }

  for (const key of ['searchForms', 'stateLevelSubdivisions', 'targetGroup'] as const) {
    const values = getStringArrayFromContainsFilter(whereFilter[key]);

    if (values.length) {
      (filterResult as Record<string, unknown>)[key] = values;
    }
  }

  const subTypes = new Set<string>();
  const subTypeForms = new Set<string>();
  const topicIds = new Set<string>();

  addValuesToSet(subTypes, getStringArrayFromInFilter(whereFilter.subType));
  addValuesToSet(subTypeForms, getStringArrayFromInFilter(whereFilter.subTypeForm));
  addValuesToSet(topicIds, getTopicIdsFromBranch(whereFilter));

  if (Array.isArray(whereFilter.or)) {
    for (const branch of whereFilter.or as WhereFilter<OrganizationSource>[]) {
      addValuesToSet(subTypes, getStringArrayFromInFilter(branch.subType));
      addValuesToSet(subTypeForms, getStringArrayFromInFilter(branch.subTypeForm));
      addValuesToSet(topicIds, getTopicIdsFromBranch(branch));
    }
  }

  if (subTypes.size) {
    filterResult.subType = Array.from(subTypes) as OrganizationSubType[];
  }

  if (subTypeForms.size) {
    filterResult.subTypeForm = Array.from(subTypeForms) as OrganizationSubTypeForm[];
  }

  if (topicIds.size) {
    filterResult.topics = Array.from(topicIds);
  }

  return filterResult;
}

export function getOrganizationSubTypeBranches(
  subTypes: OrganizationSubType[],
  subTypeForms: OrganizationSubTypeForm[],
): WhereFilter<OrganizationSource>[] {
  if (!subTypes.length) {
    if (subTypeForms.length) {
      return [
        {
          subTypeForm: {
            in: subTypeForms,
          },
        } as WhereFilter<OrganizationSource>,
      ];
    }

    return [];
  }

  const branches: WhereFilter<OrganizationSource>[] = [];
  const subTypesWithoutSelectedForms = subTypes.filter((subType) => {
    const relatedForms = FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID[subType] || [];

    if (!relatedForms.length) {
      return true;
    }

    return !subTypeForms.some((subTypeForm) => {
      return relatedForms.includes(subTypeForm);
    });
  });

  if (subTypesWithoutSelectedForms.length) {
    branches.push({
      subType: {
        in: subTypesWithoutSelectedForms,
      },
    } as WhereFilter<OrganizationSource>);
  }

  if (subTypeForms.length) {
    branches.push({
      subTypeForm: {
        in: subTypeForms,
      },
    } as WhereFilter<OrganizationSource>);
  }

  return branches;
}

export function getOrganizationTopicBranches(topicIds: string[]): WhereFilter<OrganizationSource>[] {
  return topicIds.map((id) => {
    return {
      topics: {
        id,
      },
    } as WhereFilter<OrganizationSource>;
  });
}

function combineOrBranchGroups(groups: WhereFilter<OrganizationSource>[][]): WhereFilter<OrganizationSource>[] {
  const nonEmptyGroups = groups.filter((group) => {
    return group?.length;
  });

  if (!nonEmptyGroups.length) {
    return [];
  }

  return nonEmptyGroups.reduce((combined, group) => {
    return combined.flatMap((leftBranch) => {
      return group.map((rightBranch) => {
        return {
          ...leftBranch,
          ...rightBranch,
        };
      });
    });
  }, [{} as WhereFilter<OrganizationSource>]);
}

function addValuesToSet(target: Set<string>, values: string[]): void {
  for (const value of values) {
    target.add(value);
  }
}

function getStringArrayFromContainsFilter(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }

  if (!Array.isArray((value as { contains?: unknown }).contains)) {
    return [];
  }

  return ((value as { contains: unknown[] }).contains || [])
    .filter((item): item is string => {
      return typeof item === 'string';
    });
}

function getStringArrayFromInFilter(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }

  if (!Array.isArray((value as { in?: unknown }).in)) {
    return [];
  }

  return ((value as { in: unknown[] }).in || [])
    .filter((item): item is string => {
      return typeof item === 'string';
    });
}

function getTopicIdsFromBranch(branch: WhereFilter<OrganizationSource>): string[] {
  const topic = (branch.topics as { id?: unknown }) || {};

  if (typeof topic.id !== 'string') {
    return [];
  }

  return [topic.id];
}

function getOrganizationArrayWhereFilter(filterResult: OrganizationFilterResultForWhere): WhereFilter<OrganizationSource> {
  const where: WhereFilter<OrganizationSource> = {};

  for (const key of ['qualifications', 'media', 'searchForms', 'stateLevelSubdivisions', 'targetGroup'] as const) {
    if (filterResult[key]?.length) {
      where[key] = {
        contains: filterResult[key],
      };
    }
  }

  return where;
}
